import React, { useEffect } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle, gradeTone, splitGrade } from './tokens';
import type {
  PortfolioRow, PortfolioDimension, PortfolioGroupScore, PortfolioPad, Tone,
} from '../../lib/portfolioApi';
import {
  isScoredPipelineReference, pipelineReferencePresentation, PIPELINE_REFERENCE_FLOW_SOURCE,
} from '../../lib/pipeline-reference';

interface Props {
  row: PortfolioRow | null;
  cycleScores: number[];
  onClose: () => void;
  onOpenBriefing: (parcelId: string) => void;
  onOpenMemo?: (parcelId: string) => void;
  /** Crown Pine only: the selected block's buildable pads, listed with acreage.
      Integra never passes this — the panel renders exactly as before without it. */
  pads?: PortfolioPad[];
}

const PANEL_WIDTH = 440;

export const PortfolioSidePanel: React.FC<Props> = ({
  row, cycleScores, onClose, onOpenBriefing, onOpenMemo, pads,
}) => {
  useEffect(() => {
    if (!row) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [row, onClose]);

  const open = !!row;

  return (
    <aside
      aria-label="Parcel detail"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        height: '100vh',
        width: PANEL_WIDTH,
        background: REPORT.white,
        borderLeft: `1px solid ${PORTFOLIO_TOKENS.ruleHard}`,
        boxShadow: open ? '-8px 0 24px -12px rgba(46,56,64,0.18)' : 'none',
        zIndex: 50,
        transform: open ? 'translateX(0)' : `translateX(${PANEL_WIDTH}px)`,
        transition: 'transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 220ms ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {row && <PanelBody
        row={row}
        cycleScores={cycleScores}
        onClose={onClose}
        onOpenBriefing={onOpenBriefing}
        onOpenMemo={onOpenMemo}
        pads={pads}
      />}
    </aside>
  );
};

interface PanelBodyProps {
  row: PortfolioRow;
  cycleScores: number[];
  onClose: () => void;
  onOpenBriefing: (parcelId: string) => void;
  onOpenMemo?: (parcelId: string) => void;
  pads?: PortfolioPad[];
}

const PanelBody: React.FC<PanelBodyProps> = ({ row, cycleScores, onClose, onOpenBriefing, onOpenMemo, pads }) => {
  const tone = gradeTone(row.grade);
  const style = toneStyle(tone);
  const { letter, sign } = splitGrade(row.grade);
  const atOrBelow = cycleScores.filter(s => s <= (row.score ?? 0)).length;
  const topPct = Math.max(1, 100 - Math.round((atOrBelow / Math.max(1, cycleScores.length)) * 100));
  const isPark = row.priority_tag === 'park';
  const pipeMeta = row.pipeline
    ? `on ${row.pipeline.name}${row.pipeline.diameter_in ? ` · ${row.pipeline.diameter_in}″` : ''}`
    : null;
  const reference = pipelineReferencePresentation(row);
  const scoredReference = row.score != null && isScoredPipelineReference(row) ? row.pipeline_reference : null;

  return (
    <>
      {/* Header — dense title block. */}
      <header style={{
        padding: '10px 16px 8px',
        borderBottom: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: PORTFOLIO_TOKENS.mono,
            fontSize: 8.5,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: isPark ? REPORT.fail : REPORT.accent,
            marginBottom: 2,
          }}>
            Rank {row.rank ?? '—'} · {row.state}
            {row.priority_label && (
              <>
                <span style={{ color: REPORT.tertiary, margin: '0 5px' }}>·</span>
                {row.priority_label}
              </>
            )}
          </div>
          <div style={{
            fontFamily: PORTFOLIO_TOKENS.serif,
            fontSize: 17,
            lineHeight: 1.1,
            color: REPORT.heading,
            letterSpacing: '-0.005em',
            margin: 0,
          }}>
            {row.county} County
            {row.site_label && row.site_label !== `${row.county} County` && (
              <span style={{ color: REPORT.secondary, fontSize: 13, marginLeft: 5 }}>
                {row.site_label}
              </span>
            )}
          </div>
          {pipeMeta && (
            <div style={{
              fontFamily: PORTFOLIO_TOKENS.mono,
              fontSize: 9,
              letterSpacing: '0.04em',
              color: REPORT.secondary,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {pipeMeta}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          title="Close (Esc)"
          style={{
            background: 'transparent',
            border: 'none',
            fontFamily: PORTFOLIO_TOKENS.mono,
            fontSize: 16,
            lineHeight: 1,
            color: REPORT.secondary,
            cursor: 'pointer',
            padding: '3px 5px',
            borderRadius: 3,
            transition: 'color 0.15s ease, background 0.15s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.color = REPORT.heading;
            (e.currentTarget as HTMLElement).style.background = PORTFOLIO_TOKENS.amberWash;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.color = REPORT.secondary;
            (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >×</button>
      </header>

      {/* Body — scrollable. Tight stack so every card sits within one screen
          on a 1366×768 viewport without scrolling for the common case. */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: '10px 16px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
        {row.score != null && cycleScores.length > 0 && (
          <div style={{
            padding: '8px 12px',
            borderRadius: 4,
            background: style.bg,
            border: `1px solid ${style.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{
              fontFamily: PORTFOLIO_TOKENS.serif,
              fontSize: 34,
              lineHeight: 1,
              color: style.fg,
              letterSpacing: '-0.02em',
            }}>
              {letter}
              {sign && <span style={{ fontSize: 19, opacity: 0.7, marginLeft: -1, verticalAlign: 7 }}>{sign}</span>}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{
                fontFamily: PORTFOLIO_TOKENS.mono,
                fontSize: 12,
                color: REPORT.heading,
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}>{row.score}<span style={{ color: REPORT.tertiary, fontWeight: 400 }}>/100</span></span>
              <span style={{
                fontFamily: PORTFOLIO_TOKENS.mono,
                fontSize: 8.5,
                color: REPORT.secondary,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>Top {topPct}% · {cycleScores.length} sites</span>
              <span style={{
                fontFamily: PORTFOLIO_TOKENS.mono,
                fontSize: 8.5,
                color: REPORT.secondary,
              }}>Cycle ranks may mix reference and retained score bases until coverage is complete.</span>
            </div>
          </div>
        )}

        {/* Three IC score groups — Fuel Delivery / Site Readiness / Acquisition.
            Each card carries the methodology inline: header strip with grade +
            score + percentile, then the inputs that drove the grade. */}
        <SectionLabel>How we scored</SectionLabel>
        {pads && pads.length > 0 && <p style={{ fontSize: '12px', lineHeight: 1.5, color: REPORT.secondary, margin: 0 }}>
          Assessment scope: parent block {row.carve_out?.name || row.site_label || `${row.county} County`}. Individual pad scores are not calculated in this view.
        </p>}
        {(row.score != null || reference.kind === 'unassessed') && <p style={{ fontSize: '12px', lineHeight: 1.5, color: REPORT.secondary, margin: 0 }}>
          {reference.scoreCopy}.
        </p>}
        {scoredReference && (
          <div style={{ border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`, borderRadius: 4, padding: '8px 10px', fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 10, color: REPORT.body }}>
            {row.carve_out && <div style={{ marginBottom: 6, color: REPORT.secondary, lineHeight: 1.4 }}>
              Reference measured from {row.carve_out.name}.
            </div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span>Modeled gas</span><strong>{scoredReference.gas_mmscfd.toLocaleString('en-US', { maximumFractionDigits: 2 })} MMscf/day</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 5 }}>
              <span>Continuous net MW</span><strong>{scoredReference.reference_mw.toLocaleString('en-US', { maximumFractionDigits: 2 })} MW</strong>
            </div>
            <div style={{ marginTop: 6, color: REPORT.secondary, lineHeight: 1.4 }}>
              {scoredReference.pipeline_name} · {scoredReference.diameter_inches}″ · {scoredReference.distance_miles.toLocaleString('en-US', { maximumFractionDigits: 2 })} mi
            </div>
            <div style={{ marginTop: 4, color: REPORT.secondary, lineHeight: 1.4 }}>
              20 mi hydraulic reach · 600→500 psig · recorded diameter as assumed ID · 8,805 Btu/kWh net HHV
            </div>
            <a href={PIPELINE_REFERENCE_FLOW_SOURCE} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 5, color: REPORT.accent }}>
              {reference.basisLabel} · equation source
            </a>
          </div>
        )}
        <GroupScoreCard label="Fuel Delivery"  group={row.group_scores.fuel_delivery}  inputs={fuelDeliveryInputs(row)} />
        <GroupScoreCard label="Site Readiness" group={row.group_scores.site_readiness} inputs={siteReadinessInputs(row)} />
        <GroupScoreCard label="Acquisition"    group={row.group_scores.acquisition}    inputs={acquisitionInputs(row)} />

        {/* Five Integra priority dimensions — dense table-row format so
            evidence stays visible without each row eating 70px of vertical. */}
        <SectionLabel>Integra dimensions</SectionLabel>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}>
          <DimDetailRow label="Power"     dim={row.dimensions.power} />
          <DimDetailRow label="Land"      dim={row.dimensions.land} />
          <DimDetailRow label="Ownership" dim={row.dimensions.ownership} />
          <DimDetailRow label="Fiber"     dim={row.dimensions.fiber} />
          <DimDetailRow label="Water"     dim={row.dimensions.water} />
        </div>

        {/* Crown Pine only: the block's buildable pads with their own acreage.
            Absent for Integra (pads prop never passed). */}
        {pads && pads.length > 0 && (
          <>
            <SectionLabel>Buildable pads · {pads.length}</SectionLabel>
            <div style={{
              border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
              borderRadius: 4,
              overflow: 'hidden',
            }}>
              {pads.slice().sort((a, b) => (b.acres ?? -1) - (a.acres ?? -1)).map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 8,
                  padding: '5px 10px',
                  borderTop: i === 0 ? 'none' : `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
                  fontFamily: PORTFOLIO_TOKENS.mono,
                  fontSize: 10,
                  color: REPORT.secondary,
                }}>
                  <span>
                    Pad {p.piece_index}
                    {p.site && <span style={{ color: REPORT.accent, fontWeight: 700 }}> · Site {p.site}</span>}
                  </span>
                  <span>
                    <span style={{ color: REPORT.heading, fontWeight: 600 }}>
                      {p.acres != null ? `${Math.round(p.acres).toLocaleString()} ac` : '— ac'}
                    </span>
                    {p.avg_slope_pct != null && <span> · {Number(p.avg_slope_pct).toFixed(1)}%</span>}
                  </span>
                </div>
              ))}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '5px 10px',
                borderTop: `1px solid ${PORTFOLIO_TOKENS.ruleHard}`,
                background: REPORT.surface,
                fontFamily: PORTFOLIO_TOKENS.mono,
                fontSize: 10,
                fontWeight: 700,
                color: REPORT.heading,
              }}>
                <span>Total buildable</span>
                <span>{Math.round(pads.reduce((s, p) => s + (p.acres ?? 0), 0)).toLocaleString()} ac</span>
              </div>
            </div>
          </>
        )}

      </div>

      <footer style={{
        padding: '10px 16px',
        borderTop: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
        background: REPORT.surface,
        display: 'flex',
        gap: 8,
        flexShrink: 0,
      }}>
        <ActionButton
          label="View Memo"
          tone="primary"
          disabled={!onOpenMemo}
          fullWidth
          onClick={() => onOpenMemo?.(row.parcel_id)}
        />
        <ActionButton
          label="View Briefing"
          tone="secondary"
          fullWidth
          onClick={() => onOpenBriefing(row.parcel_id)}
        />
      </footer>
    </>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontFamily: PORTFOLIO_TOKENS.mono,
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: REPORT.heading,
    marginTop: 2,
    marginBottom: -2,
  }}>{children}</div>
);

// A scoring input that drove one of the three group grades. Mirrors the
// methodology block from the briefing's DimensionBar, displayed inline in
// the side-panel group card.
interface GroupInput {
  label: string;
  weight: string;
  value: string;
  tone: Tone;
}

const GroupScoreCard: React.FC<{
  label: string;
  group: PortfolioGroupScore;
  inputs: GroupInput[];
}> = ({ label, group, inputs }) => {
  const tone: Tone = gradeTone(group.grade);
  const style = toneStyle(tone);
  const { letter, sign } = splitGrade(group.grade);
  const hasScore = group.score != null;
  return (
    <div style={{
      padding: '8px 10px 7px',
      borderRadius: 4,
      background: style.bg,
      border: `1px solid ${style.border}`,
    }}>
      {/* Header strip — label + grade + score + percentile + histogram. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: inputs.length > 0 ? 6 : 0,
      }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: style.fg,
          flexShrink: 0,
          width: 88,
        }}>{label}</span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.serif,
          fontSize: 22,
          lineHeight: 1,
          color: style.fg,
          letterSpacing: '-0.01em',
          flexShrink: 0,
          width: 32,
        }}>
          {letter}
          {sign && <span style={{ fontSize: 13, opacity: 0.7, marginLeft: -1, verticalAlign: 4 }}>{sign}</span>}
        </span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 10,
          fontWeight: 600,
          color: REPORT.heading,
          letterSpacing: '0.02em',
          flexShrink: 0,
        }}>
          {hasScore ? group.score : '—'}
          <span style={{ color: REPORT.tertiary, fontWeight: 400 }}>/100</span>
        </span>
        {group.percentile != null && (
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono,
            fontSize: 8.5,
            color: REPORT.secondary,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}>
            P{Math.round(group.percentile)}
          </span>
        )}
      </div>

      {/* Inputs table — the "how we score" detail. 3 columns: name · weight · value. */}
      {inputs.length > 0 && (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: PORTFOLIO_TOKENS.sans,
        }}>
          <tbody>
            {inputs.map((inp, i) => {
              const valTone = inp.tone === 'neutral' ? null : toneStyle(inp.tone);
              return (
                <tr key={inp.label} style={{
                  borderTop: i === 0 ? `1px solid ${PORTFOLIO_TOKENS.ruleSoft}` : 'none',
                  borderBottom: i < inputs.length - 1 ? `1px solid ${PORTFOLIO_TOKENS.ruleSoft}` : 'none',
                }}>
                  <td style={{
                    padding: '3px 0',
                    fontSize: 10.5,
                    color: REPORT.heading,
                    fontWeight: 500,
                  }}>{inp.label}</td>
                  <td style={{
                    padding: '3px 6px 3px 0',
                    fontFamily: PORTFOLIO_TOKENS.mono,
                    fontSize: 8.5,
                    color: REPORT.tertiary,
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                    width: '14%',
                  }}>{inp.weight}</td>
                  <td style={{
                    padding: '3px 0',
                    fontFamily: PORTFOLIO_TOKENS.mono,
                    fontSize: 9.5,
                    color: valTone ? valTone.fg : REPORT.body,
                    fontWeight: valTone ? 600 : 500,
                    letterSpacing: '0.02em',
                    textAlign: 'right',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 180,
                  }}>{inp.value}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Per-dimension dense row. Single line: label · grade · driver. Evidence
// bullets render below at 10.5px / 1.35 line-height — much tighter than the
// previous full-card-per-dimension layout. Whole block is wrapped in a soft
// tone tint so the eye can still pick out passes vs. fails.
const DimDetailRow: React.FC<{ label: string; dim: PortfolioDimension }> = ({ label, dim }) => {
  const style = toneStyle(dim.tone);
  const { letter, sign } = splitGrade(dim.grade);
  return (
    <div style={{
      padding: '6px 10px',
      borderRadius: 3,
      background: style.bg,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: dim.evidence && dim.evidence.length > 0 ? 3 : 0,
      }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 8.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: style.fg,
          width: 70,
          flexShrink: 0,
        }}>{label}</span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.serif,
          fontSize: 16,
          lineHeight: 1,
          color: style.fg,
          letterSpacing: '-0.01em',
          width: 26,
          flexShrink: 0,
        }}>
          {letter}
          {sign && <span style={{ fontSize: 10, opacity: 0.7, marginLeft: -1, verticalAlign: 3 }}>{sign}</span>}
        </span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 9.5,
          color: REPORT.tertiary,
          letterSpacing: '0.02em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>{dim.driver}</span>
      </div>
      {dim.evidence && dim.evidence.length > 0 && (
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          fontFamily: PORTFOLIO_TOKENS.sans,
          fontSize: 10,
          lineHeight: 1.35,
          color: REPORT.body,
        }}>
          {dim.evidence.map((e, i) => (
            <li key={i} style={{
              position: 'relative',
              paddingLeft: 9,
              marginTop: i === 0 ? 0 : 2,
            }}>
              <span style={{
                position: 'absolute',
                left: 0,
                top: 6,
                width: 3,
                height: 3,
                borderRadius: 2,
                background: REPORT.tertiary,
              }} />
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ActionButton: React.FC<{
  label: string;
  tone: 'primary' | 'secondary';
  disabled?: boolean;
  fullWidth?: boolean;
  onClick: () => void;
}> = ({ label, tone, disabled, fullWidth, onClick }) => {
  const isPrimary = tone === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: PORTFOLIO_TOKENS.mono,
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '7px 12px',
        borderRadius: 4,
        border: isPrimary ? `1px solid ${REPORT.accent}` : `1px solid ${REPORT.rule}`,
        background: isPrimary ? REPORT.accent : 'transparent',
        color: isPrimary ? REPORT.white : REPORT.heading,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        flex: fullWidth ? 1 : '0 0 auto',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={e => {
        if (disabled) return;
        const el = e.currentTarget as HTMLElement;
        if (isPrimary) {
          el.style.boxShadow = '0 3px 10px -4px rgba(196,112,90,0.55)';
        } else {
          el.style.borderColor = REPORT.heading;
        }
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow = 'none';
        if (!isPrimary) el.style.borderColor = REPORT.rule;
      }}
    >
      {label}
    </button>
  );
};

// ── Input builders — one per IC group. Each function returns the rows the
// group's score card renders, mirroring the weights in scripts/lib/scoring.js.
// Tones are inferred from the underlying data so the IC can scan strengths
// (green) and risks (amber/red) at a glance.

function fuelDeliveryInputs(row: PortfolioRow): GroupInput[] {
  const mw = row.key_numbers.available_mw;
  const infraGrade = row.dimensions.power.tone;
  const corridor = row.corridor;
  return [
    {
      label: 'Pipeline MW',
      weight: '65%',
      value: mw == null ? 'unknown' : `${mw.toLocaleString()} MW`,
      tone: mw == null ? 'neutral'
        : mw >= 1000 ? 'pass'
        : mw >= 200 ? 'caution'
        : 'fail',
    },
    {
      label: 'Infra (corridor)',
      weight: '35%',
      value: row.pipeline?.diameter_in
        ? `${row.pipeline.name} (${row.pipeline.diameter_in}″)`
        : (corridor?.label || 'unknown'),
      tone: infraGrade,
    },
  ];
}

function siteReadinessInputs(row: PortfolioRow): GroupInput[] {
  const acres = row.key_numbers.acres;
  const slope = row.avg_slope_pct;
  const floodPct = row.flood_zone_pct_100yr;
  const air = row.key_numbers.air_permit;
  const fiber = row.dimensions.fiber;

  return [
    {
      label: 'Acreage',
      weight: '25%',
      value: acres == null ? 'unknown'
        : `${acres.toLocaleString()} ac${row.clean_pad_acres != null ? ` · ${row.clean_pad_acres.toLocaleString()} clean` : ''}`,
      tone: acres == null ? 'neutral'
        : acres >= 2500 ? 'pass'
        : acres >= 1000 ? 'caution'
        : 'fail',
    },
    {
      label: 'Avg slope',
      weight: '25%',
      value: slope == null ? 'unknown' : `${slope.toFixed(1)}%${slope >= 10 ? ' · knockout' : ''}`,
      tone: slope == null ? 'neutral'
        : slope >= 10 ? 'fail'
        : slope >= 5 ? 'caution'
        : 'pass',
    },
    {
      label: 'EPA',
      weight: '25%',
      value: air.value,
      tone: air.tone,
    },
    {
      label: 'Flood',
      weight: '10%',
      value: floodPct == null ? 'unknown' : floodPct < 1 ? 'clean' : `${floodPct.toFixed(0)}% in floodplain`,
      tone: floodPct == null ? 'neutral'
        : floodPct >= 20 ? 'fail'
        : floodPct >= 5 ? 'caution'
        : 'pass',
    },
    {
      label: 'Fiber',
      weight: '10%',
      value: fiber.driver,
      tone: fiber.tone,
    },
  ];
}

function acquisitionInputs(row: PortfolioRow): GroupInput[] {
  const ownership = row.dimensions.ownership;
  return [
    {
      label: 'Ownership',
      weight: '70%',
      value: ownership.driver,
      tone: ownership.tone,
    },
    {
      label: 'Land value',
      weight: '15%',
      value: '—',
      tone: 'neutral',
    },
    {
      label: 'Active listing',
      weight: '15%',
      value: row.carve_out ? 'verify in briefing' : '—',
      tone: 'neutral',
    },
  ];
}

export default PortfolioSidePanel;
